import { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Banknote } from 'lucide-react';
import { segurosApi } from '../../../lib/api';
import { fmtFecha } from '../../../lib/utils';
import { EgresoAlegraDialog } from './EgresoAlegraDialog';

const fmtCLP = (v) => (v == null ? '' : Math.round(v).toLocaleString('es-CL'));
const fmtUF = (v) => (v == null ? '' : Number(v).toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

export function MatrizPrimas({ onChanged }) {
  const [data, setData] = useState(null);
  const [moneda, setMoneda] = useState('pesos');
  const [filtro, setFiltro] = useState('pendientes');
  const [egresoGrupo, setEgresoGrupo] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await segurosApi.matriz();
      setData(res.data);
    } catch { setData(null); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const visible = useCallback((c) => !!c && (filtro === 'todas' || c.estado !== 'pagada'), [filtro]);

  const subtotales = useMemo(() => {
    if (!data) return {};
    const out = {};
    for (const g of data.grupos) {
      const sub = {};
      for (const p of g.polizas) {
        for (const [ym, c] of Object.entries(p.celdas)) {
          if (!visible(c)) continue;
          sub[ym] = sub[ym] || { uf: 0, clp: 0 };
          sub[ym].uf += c.uf;
          sub[ym].clp += c.clp || 0;
        }
      }
      out[g.aseguradora] = sub;
    }
    return out;
  }, [data, visible]);

  const totalGeneral = useMemo(() => {
    const out = {};
    for (const sub of Object.values(subtotales)) {
      for (const [ym, s] of Object.entries(sub)) {
        out[ym] = out[ym] || { uf: 0, clp: 0 };
        out[ym].uf += s.uf;
        out[ym].clp += s.clp;
      }
    }
    return out;
  }, [subtotales]);

  const tieneVencidas = useMemo(
    () => !!data && data.grupos.some((g) => g.polizas.some((p) => p.celdas.vencidas)),
    [data],
  );

  if (!data) return <div className="text-sm text-muted-foreground py-8 text-center">Cargando matriz…</div>;

  const val = (c) => (moneda === 'pesos' ? fmtCLP(c.clp) : fmtUF(c.uf));
  const grupos = data.grupos.filter((g) => g.polizas.some((p) => Object.values(p.celdas).some(visible)));
  const columnas = tieneVencidas ? [{ ym: 'vencidas', label: 'Vencidas ⚠' }, ...data.meses] : data.meses;

  const vigenciaCubre = (p, ym) => p.vigencia && ym <= p.vigencia.slice(0, 7);

  return (
    <Card data-testid="matriz-primas">
      <CardContent className="pt-4 space-y-3">
        <div>
          <h2 className="text-base font-bold text-[#003366]">Distribución de Primas de Seguro a Pagar — Chirino Steel</h2>
          <div className="text-[11px] text-muted-foreground mt-1 space-y-0.5">
            <div>Cuotas vencidas con UF de hoy: {fmtFecha(data.hoy)} · Valor ${Number(data.uf_hoy?.valor || 0).toLocaleString('es-CL', { minimumFractionDigits: 2 })}</div>
            <div>Montos hasta el {fmtFecha(data.uf_limite)} se presentan con la UF correspondiente al día de su vencimiento. Vencimientos posteriores se calculan con la UF de hoy (provisorio ~).</div>
          </div>
        </div>
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2 text-xs">
            <span className="font-medium">Informe</span>
            <div className="flex border rounded overflow-hidden">
              <button className={`px-3 py-1 ${moneda === 'pesos' ? 'bg-green-300 font-semibold' : 'bg-white'}`} onClick={() => setMoneda('pesos')} data-testid="toggle-pesos">En Pesos</button>
              <button className={`px-3 py-1 border-l ${moneda === 'uf' ? 'bg-green-300 font-semibold' : 'bg-white'}`} onClick={() => setMoneda('uf')} data-testid="toggle-uf">En UF</button>
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <span className="font-medium">Mostrar</span>
            <div className="flex border rounded overflow-hidden">
              <button className={`px-3 py-1 ${filtro === 'pendientes' ? 'bg-amber-200 font-semibold' : 'bg-white'}`} onClick={() => setFiltro('pendientes')} data-testid="toggle-pendientes">Solo pendientes</button>
              <button className={`px-3 py-1 border-l ${filtro === 'todas' ? 'bg-amber-200 font-semibold' : 'bg-white'}`} onClick={() => setFiltro('todas')} data-testid="toggle-todas">Todas las cuotas</button>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto border rounded">
          <table className="text-xs w-full" style={{ minWidth: 1100 }}>
            <thead>
              <tr className="bg-muted/60 border-b">
                <th className="text-left px-2 py-1.5 sticky left-0 bg-muted/60 min-w-[230px]">
                  <div>Aseguradora</div>
                  <div className="font-normal text-muted-foreground">Bienes asegurados</div>
                </th>
                {columnas.map((mth) => (
                  <th key={mth.ym} className={`px-2 py-1.5 text-right min-w-[68px] border-l ${mth.ym === 'vencidas' ? 'text-red-600' : ''}`}>{mth.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {grupos.map((g) => (
                <GrupoRows key={g.aseguradora} g={g} meses={columnas} val={val} visible={visible}
                  subtotal={subtotales[g.aseguradora] || {}} moneda={moneda} vigenciaCubre={vigenciaCubre}
                  onEgreso={() => setEgresoGrupo(g)} />
              ))}
              {grupos.length === 0 && (
                <tr><td colSpan={columnas.length + 1} className="text-center py-6 text-muted-foreground">Sin cuotas para mostrar</td></tr>
              )}
            </tbody>
            {grupos.length > 0 && (
              <tfoot>
                <tr className="border-t-2 border-[#003366] bg-[#003366]/5 font-bold" data-testid="matriz-total-general">
                  <td className="px-2 py-2 sticky left-0 bg-[#e8edf3] text-[#003366]">TOTAL GENERAL</td>
                  {columnas.map((m) => {
                    const t = totalGeneral[m.ym];
                    return (
                      <td key={m.ym} className={`px-2 py-2 text-right border-l text-[#003366] ${m.ym === 'vencidas' ? 'text-red-700' : ''}`}>
                        {t ? (moneda === 'pesos' ? fmtCLP(t.clp) : fmtUF(t.uf)) : ''}
                      </td>
                    );
                  })}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </CardContent>

      {egresoGrupo && (
        <EgresoAlegraDialog grupo={egresoGrupo} ufHoy={data.uf_hoy} hoy={data.hoy}
          onClose={() => setEgresoGrupo(null)}
          onDone={() => { setEgresoGrupo(null); load(); onChanged?.(); }} />
      )}
    </Card>
  );
}

function GrupoRows({ g, meses, val, visible, subtotal, moneda, vigenciaCubre, onEgreso }) {
  const tienePendientes = g.polizas.some((p) => Object.values(p.celdas).some((c) => c.estado !== 'pagada'));
  return (
    <>
      <tr className="border-b bg-white font-bold" data-testid={`matriz-grupo-${g.aseguradora}`}>
        <td className="px-2 py-1.5 sticky left-0 bg-white">
          <div className="flex items-center justify-between gap-2">
            <span>{g.aseguradora}</span>
            {tienePendientes && (
              <Button variant="outline" size="sm" className="h-6 text-[10px] px-2" onClick={onEgreso}
                data-testid={`btn-egreso-${g.aseguradora}`}>
                <Banknote className="w-3 h-3 mr-1" /> Pagar / Egreso
              </Button>
            )}
          </div>
        </td>
        {meses.map((m) => {
          const s = subtotal[m.ym];
          return <td key={m.ym} className="px-2 py-1.5 text-right border-l">{s ? (moneda === 'pesos' ? fmtCLP(s.clp) : fmtUF(s.uf)) : ''}</td>;
        })}
      </tr>
      {g.polizas.map((p) => {
        const alguna = Object.values(p.celdas).some(visible);
        if (!alguna) return null;
        return (
          <tr key={p.id} className="border-b hover:bg-muted/20" data-testid={`matriz-poliza-${p.numero_poliza}`}>
            <td className="px-2 py-1 sticky left-0 bg-blue-50/70">
              <div className="font-medium">{p.patente && !(p.bien_asegurado || '').toUpperCase().includes(p.patente.toUpperCase()) ? `${p.patente} ` : ''}{p.bien_asegurado}</div>
              {p.vigencia && <div className="text-[10px] text-muted-foreground">Vigencia: <b>{fmtFecha(p.vigencia)}</b> · Pól. {p.numero_poliza}</div>}
              {!p.vigencia && <div className="text-[10px] text-muted-foreground">Pól. {p.numero_poliza}</div>}
            </td>
            {meses.map((m) => {
              const c = p.celdas[m.ym];
              const show = visible(c);
              const enVigencia = m.ym !== 'vencidas' && vigenciaCubre(p, m.ym);
              return (
                <td key={m.ym} className={`px-2 py-1 text-right align-top border-l ${enVigencia ? 'border-b-4 border-b-green-400' : ''}`}>
                  {show && (
                    <div title={`Vence ${fmtFecha(c.fecha)}${c.provisorio ? ' · UF provisoria' : ''}`}>
                      <div className={`${c.estado === 'pagada' ? 'text-emerald-700' : c.vencida ? 'text-red-600 font-semibold' : ''}`}>
                        {val(c)}{c.provisorio ? '~' : ''}
                      </div>
                      <div className="text-[9px] italic text-muted-foreground">{c.cuota}{c.estado === 'pagada' ? ' ✓' : ''}</div>
                    </div>
                  )}
                </td>
              );
            })}
          </tr>
        );
      })}
      <tr><td colSpan={meses.length + 1} className="py-1"></td></tr>
    </>
  );
}
